import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { API_URL } from "src/Utils/API";

export interface AccountRequest {
  id: string;
  firstName: string;
  lastName: string;
  phone: string;
  email: string;
  restaurantName: string;
  cityName: string;
  subscriptionTier: string;
  status: "PENDING" | "APPROVED" | "REJECTED";
  message: string | null;
  createdAt: string;
}

export interface ApproveAccountRequestPayload {
  requestId: string;
  restaurantId: string;
}

export interface RejectAccountRequestPayload {
  id: string;
  reason: string;
}

const requestKeys = {
  all: ["account-requests"] as const,
};

// GET /web/requests — Admin only, returns raw AccountRequest[]
export const useAccountRequests = () =>
  useQuery({
    queryKey: requestKeys.all,
    queryFn: async (): Promise<AccountRequest[]> => {
      const res = await fetch(`${API_URL}/requests`, {
        credentials: "include",
      });
      if (!res.ok) throw new Error("Failed to fetch account requests");
      const body = await res.json();

      if (!body.success) {
        throw new Error(body.message ?? "Failed to fetch account requests");
      }

      return body.data as AccountRequest[];
    },
  });

// POST /web/staff/approve-request — Admin only
export const useApproveAccountRequest = (onSuccess?: () => void) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (
      payload: ApproveAccountRequestPayload,
    ): Promise<void> => {
      const res = await fetch(`${API_URL}/requests/approve`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const body = await res.json();
      if (!body.success)
        throw new Error(body.message ?? "Failed to approve request");
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: requestKeys.all });
      onSuccess?.();
    },
  });
};

// POST /web/requests/reject (placeholder — adjust path when backend is ready)
export const useRejectAccountRequest = (onSuccess?: () => void) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: RejectAccountRequestPayload): Promise<void> => {
      const res = await fetch(`${API_URL}/requests/reject`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          message: payload.reason,
          requestId: payload.id,
        }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(
          (body as { message?: string }).message ?? "Failed to reject request",
        );
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: requestKeys.all });
      onSuccess?.();
    },
  });
};
